import { useMemo } from 'react';
import type { CashFlowSummary } from '../../services/cashFlowCalculations';
import { formatCurrency, formatMonth } from '../../services/cashFlowCalculations';

interface CashFlowChartProps {
  summary: CashFlowSummary;
}

export function CashFlowChart({ summary }: CashFlowChartProps) {
  const { chartData, maxValue } = useMemo(() => {
    const months = Array.from(summary.byMonth.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(0, 12); // Mostra max 12 mesi

    const maxVal = Math.max(
      ...months.map(([, data]) => Math.max(data.entrate, data.uscite)),
      1
    );

    return {
      chartData: months,
      maxValue: maxVal
    };
  }, [summary]);

  if (chartData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-gray-500">Nessun dato da visualizzare</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Flussi Mensili
      </h3>

      <div className="space-y-4">
        {chartData.map(([monthKey, data]) => {
          const entrateWidth = (data.entrate / maxValue) * 100;
          const usciteWidth = (data.uscite / maxValue) * 100;

          return (
            <div key={monthKey}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-gray-700 capitalize">
                  {formatMonth(monthKey)}
                </span>
                <span className={`font-medium ${data.saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {data.saldo >= 0 ? '+' : ''}{formatCurrency(data.saldo)}
                </span>
              </div>

              <div className="relative h-8 flex gap-1">
                {/* Entrate */}
                <div className="flex-1 bg-gray-100 rounded-l-md overflow-hidden">
                  <div
                    className="h-full bg-green-500 transition-all duration-300"
                    style={{ width: `${entrateWidth}%` }}
                    title={`Entrate: ${formatCurrency(data.entrate)}`}
                  />
                </div>

                {/* Uscite */}
                <div className="flex-1 bg-gray-100 rounded-r-md overflow-hidden">
                  <div
                    className="h-full bg-red-400 transition-all duration-300 ml-auto"
                    style={{ width: `${usciteWidth}%` }}
                    title={`Uscite: ${formatCurrency(data.uscite)}`}
                  />
                </div>
              </div>

              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>+ {formatCurrency(data.entrate)}</span>
                <span>- {formatCurrency(data.uscite)}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-4 border-t flex justify-center gap-8 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 rounded" />
          <span className="text-gray-600">Entrate</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-400 rounded" />
          <span className="text-gray-600">Uscite</span>
        </div>
      </div>
    </div>
  );
}
